import { useMemo, useState } from 'react';

import {
  AvatarProps,
  EmptySectionCard,
  FAIconName,
  FireflyButton,
  InquiryDetailSectionHeader,
  SkeletonLoader,
  TagColor,
  TaskInfoCard,
} from '@equip.health/ui';

import { Section } from '~/components/common/Section';
import { TaskStatusLabel } from '~/lib/util/task.util';
import { getTooltipWrapper } from '../maud/inquiries/InquiryDetail/viewUtil';
import VoidTaskModalV2 from '../maud/tasks/VoidTaskModalV2';
import { TaskEvents } from '~/analytics/taskEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

export enum DischargePlanTaskType {
  PREPPING = 'PREPPING',
  SIGNED = 'SIGNED',
  USER = 'USER',
}

export interface TaskCardProps {
  dischargePlanTaskType?: DischargePlanTaskType;
  assignees: AvatarProps[];
  category: string;
  createdDate?: string;
  dueDate?: Date;
  id: string;
  status: string;
  subCategory?: string;
  title: string;
}

export interface TasksProps {
  id: string;
  isLoading: boolean;
  onAdd: () => void;
  onChangeStatus: (
    taskId: string,
    status: string,
    voidReasonExternalId?: string,
  ) => void;
  tasks: TaskCardProps[];
  buttonOptions?: {
    showMoreButtonText?: string;
    hideMoreButtonText?: string;
  };
  patientId?: string;
}

export const statusOptions = [
  {
    color: TagColor.marigold,
    id: 'task-status__todo',
    value: TaskStatusLabel.TO_DO,
  },
  {
    color: TagColor.pink,
    id: 'task-status__in-progress',
    value: TaskStatusLabel.IN_PROGRESS,
  },
  {
    color: TagColor.red,
    id: 'task-status__void',
    value: TaskStatusLabel.VOID,
  },
  {
    color: TagColor.green,
    id: 'task-status__done',
    value: TaskStatusLabel.DONE,
  },
];

const isActiveStatus = (task: TaskCardProps) =>
  task.status == TaskStatusLabel.TO_DO ||
  task.status == TaskStatusLabel.IN_PROGRESS;

type UpdatingTaskProps = {
  id: string;
  status: string;
};

const PROVIDER = 'provider';

export const Tasks = ({
  id,
  isLoading,
  tasks,
  onAdd,
  onChangeStatus,
  buttonOptions = {},
  patientId,
}: TasksProps) => {
  const {
    showMoreButtonText = 'Show completed tasks',
    hideMoreButtonText = 'Hide completed tasks',
  } = buttonOptions;
  const TOP_LEVEL_ID = `${id}__tasks-section`;
  const { trackEvent } = useAnalytics();
  const [voidReason, setVoidReason] = useState<string>('');
  const [updatingTask, setUpdatingTask] = useState<UpdatingTaskProps>();
  const [isVoidStatusModalOpen, setIsVoidStatusModalOpen] = useState(false);
  const [enableShowMoreButton, setEnableShowMoreButton] =
    useState<boolean>(false);

  const closeVoidStatusModal = () => {
    setUpdatingTask(null);
    setVoidReason(null);
    setIsVoidStatusModalOpen(false);
  };

  const updateTaskStatus = async (taskId: string, statusId?: string) => {
    const updatedStatus = statusOptions.find((option) => {
      return option.id === statusId;
    });
    setUpdatingTask({
      ...updatingTask,
      id: taskId,
      status: updatedStatus.value,
    });
    if (updatedStatus.value === TaskStatusLabel.VOID) {
      setIsVoidStatusModalOpen(true);
    } else {
      onChangeStatus(taskId, updatedStatus.value, voidReason);
    }
  };

  const numActiveTasks = useMemo<number>(() => {
    return tasks.reduce((acc, cur) => {
      if (isActiveStatus(cur)) {
        return acc + 1;
      }
      return acc;
    }, 0);
  }, [tasks]);

  const showButton = numActiveTasks < tasks.length;

  const taskCards = useMemo(() => {
    const tasksToShow = enableShowMoreButton
      ? tasks
      : tasks.filter(isActiveStatus);

    return tasksToShow.length > 0 ? (
      tasksToShow.map((task) => {
        const status =
          updatingTask?.id === task.id ? updatingTask.status : task.status;
        const tooltipWrapper =
          task?.assignees?.[0]?.userType.toLowerCase() === PROVIDER &&
          getTooltipWrapper(task?.assignees?.[0]?.id, task?.createdDate);
        return (
          <TaskInfoCard
            key={task.id}
            onChangeStatus={(statusId: string) =>
              updateTaskStatus(task.id, statusId)
            }
            statusOptions={statusOptions}
            {...task}
            status={status}
            tooltipWrapper={tooltipWrapper}
          />
        );
      })
    ) : (
      <EmptySectionCard
        caption={
          showButton ? 'No tasks to display' : 'No tasks have been created'
        }
        id={TOP_LEVEL_ID}
      />
    );
  }, [enableShowMoreButton, tasks, updatingTask]);

  const handleShowMoreButtonClick = () => {
    const event = enableShowMoreButton
      ? TaskEvents.hideCompleteOrVoidedSelectedFromDischargePlan
      : TaskEvents.showCompleteOrVoidedSelectedFromDischargePlan;
    trackEvent(event, {
      source: AnalyticsSourceValues.DISCHARGE_PLAN_TAB,
      taskId: updatingTask?.id,
      patientId,
    });
    setEnableShowMoreButton((prev) => !prev);
  };

  return (
    <Section id={TOP_LEVEL_ID}>
      <InquiryDetailSectionHeader
        count={numActiveTasks}
        countDescription="active"
        id={`${TOP_LEVEL_ID}-header`}
        title="Tasks"
      >
        <FireflyButton
          id={`${TOP_LEVEL_ID}-add-button`}
          isDisabled={isLoading}
          leftIcon={FAIconName.plusSolid}
          onClick={onAdd}
          size="small"
          variant="secondary"
        >
          Add Task
        </FireflyButton>
      </InquiryDetailSectionHeader>
      <SkeletonLoader id={TOP_LEVEL_ID} isLoaded={!isLoading}>
        <Section id={`${TOP_LEVEL_ID}-children`}>{taskCards}</Section>
      </SkeletonLoader>
      {showButton && (
        <FireflyButton
          id={`${id}__expand__collapse`}
          onClick={handleShowMoreButtonClick}
          rightIcon={
            enableShowMoreButton
              ? FAIconName.chevronUpRegular
              : FAIconName.chevronDownRegular
          }
          size="small"
          variant="tertiary"
        >
          {enableShowMoreButton ? hideMoreButtonText : showMoreButtonText}
        </FireflyButton>
      )}
      <VoidTaskModalV2
        closeModal={closeVoidStatusModal}
        currentTask={updatingTask}
        id={TOP_LEVEL_ID}
        isOpen={isVoidStatusModalOpen}
        onChangeStatus={onChangeStatus}
        setVoidReason={setVoidReason}
        voidReason={voidReason}
      />
    </Section>
  );
};
